import { useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { SelectNative } from "../components/ui/select-native";
import { CurrencyHint } from "../components/CurrencyHint";
import { useProfile } from "../context/ProfileContext";
import { useAssumptions } from "../context/AssumptionsContext";
import { REPORTS, getReportByKey } from "../lib/reports";
import { representativeAnnualIncome } from "../lib/profileOptions";
import { downloadReportPDF } from "../lib/api";

function prefill(field, profile) {
  if (!profile) return field.default;
  if (field.profileKey === "annualIncome") {
    const v = representativeAnnualIncome(profile.income_range);
    return v ?? field.default;
  }
  if (field.profileKey === "netWorth") return profile.approx_net_worth ?? field.default;
  return field.default;
}

function ReportForm({ report }) {
  const { profile } = useProfile();
  const { assumptions } = useAssumptions();
  const [values, setValues] = useState(() => {
    const init = {};
    for (const f of report.fields) init[f.key] = prefill(f, profile);
    return init;
  });
  const [generating, setGenerating] = useState(false);

  // Live preview of the section titles this report will contain.
  const preview = useMemo(() => {
    try {
      return report.build(profile, assumptions, coerce(report, values)).sections.map((s) => s.title);
    } catch {
      return [];
    }
  }, [report, profile, assumptions, values]);

  function setField(key, raw) {
    setValues((prev) => ({ ...prev, [key]: raw }));
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      const payload = report.build(profile, assumptions, coerce(report, values));
      await downloadReportPDF(payload);
      toast.success("Report generated.");
    } catch (err) {
      toast.error("Couldn't generate the report. Is the backend running?");
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{report.title}</CardTitle>
          <CardDescription>{report.description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
            This report is educational only — not personalised investment advice. Every figure is
            illustrative and based on the assumptions you set.
          </div>
          {!profile && (
            <p className="text-sm text-muted">
              Tip: <Link to="/profile" className="text-gold underline">create your profile</Link> to
              pull in your goals, income, and net worth automatically.
            </p>
          )}
          {report.fields.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <Label htmlFor={field.key}>
                {field.label}
                {field.type === "percent" ? " (%)" : ""}
              </Label>
              {field.type === "select" ? (
                <SelectNative id={field.key} value={values[field.key]} onChange={(e) => setField(field.key, e.target.value)}>
                  {field.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </SelectNative>
              ) : (
                <>
                  <Input
                    id={field.key}
                    type="number"
                    value={values[field.key]}
                    onChange={(e) => setField(field.key, e.target.value)}
                  />
                  {field.type === "currency" && <CurrencyHint value={values[field.key]} />}
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Report contents</CardTitle>
          <CardDescription>{preview.length} sections, generated as a branded multi-page PDF.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <ol className="flex flex-col gap-1.5 text-sm">
            {preview.map((title, idx) => (
              <li key={`report-section-${title}`} className="flex gap-2">
                <span className="text-muted">{idx + 1}.</span>
                <span className="text-text">{title}</span>
              </li>
            ))}
          </ol>
          <Button onClick={handleGenerate} disabled={generating} data-testid="generate-report">
            {generating ? "Generating…" : "Generate PDF report"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/** Coerce numeric/percent form strings to numbers for the builder; selects stay as strings. */
function coerce(report, values) {
  const out = {};
  for (const f of report.fields) {
    out[f.key] = f.type === "select" ? values[f.key] : Number(values[f.key]) || 0;
  }
  return out;
}

export function ReportsPage() {
  const { reportKey } = useParams();
  const navigate = useNavigate();
  const report = reportKey ? getReportByKey(reportKey) : null;

  if (reportKey && !report) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <p className="text-sm text-muted">Unknown report.</p>
        <Link to="/reports" className="text-gold underline">Back to reports</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <button type="button" onClick={() => navigate(-1)} className="text-sm text-muted hover:text-text" data-testid="page-back">← Go back</button>
      <h1 className="mt-2 text-2xl font-semibold text-text">Reports</h1>
      <p className="mt-1 text-sm text-muted">
        Client-ready, multi-page PDF reports assembled from your profile and a few inputs.
        Educational only — not investment advice.
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            onClick={() => navigate(`/reports/${r.key}`)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:border-gold ${
              report?.key === r.key ? "border-gold text-text" : "border-border text-muted"
            }`}
          >
            {r.title}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {report ? (
          <ReportForm report={report} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {REPORTS.map((r) => (
              <button key={r.key} onClick={() => navigate(`/reports/${r.key}`)} className="text-left">
                <Card className="h-full transition-colors hover:border-gold">
                  <CardHeader>
                    <CardTitle className="text-base">{r.title}</CardTitle>
                    <CardDescription>{r.description}</CardDescription>
                  </CardHeader>
                </Card>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
